import { AsyncPipe, NgFor, NgIf, isPlatformServer } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router, RouterOutlet } from '@angular/router';
import {
  AssetSectionDto,
  EmbedSectionDto,
  GalleryDto,
  GallerySectionDto,
  SectionDto,
  TenantDto,
  TextSectionDto,
} from '@picflow/types';
import { isDarkColor } from 'src/app/lib/is-dark-color';
import { loadFont } from 'src/app/lib/load-font';
import { parseColor } from 'src/app/lib/parse-color';
import { sleep } from 'src/app/lib/sleep';
import { BrowserService } from 'src/app/services/browser.service';
import { CleanUserGeneratedHtmlService } from 'src/app/services/clean-user-generated-html.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { VideoViewerComponent } from '../../../gallery-shared/components/video-viewer/video-viewer.component';
import { GalleryCoverUrlPipe } from '../../../shared/pipes/gallery-cover-url.pipe';
import { AlbumSectionComponent } from '../../components/album-section/album-section.component';
import { AssetsSectionComponent } from '../../components/assets-section/assets-section.component';
import { EmbedSectionComponent } from '../../components/embed-section/embed-section.component';
import { TenantIconLoaderComponent } from '../../components/tenant-icon-loader/tenant-icon-loader.component';
import { TextSectionComponent } from '../../components/text-section/text-section.component';

@Component({
  selector: 'app-embed-gallery-page',
  templateUrl: './embed-gallery-page.component.html',
  styleUrls: ['./embed-gallery-page.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    NgIf,
    NgFor,
    AlbumSectionComponent,
    TextSectionComponent,
    AssetsSectionComponent,
    EmbedSectionComponent,
    TenantIconLoaderComponent,
    GalleryCoverUrlPipe,
    AsyncPipe,
    VideoViewerComponent,
  ],
})
export class EmbedGalleryPageComponent implements OnInit {
  gallery: GalleryDto;
  tenant: TenantDto;
  sections: SectionDto[];
  platformBrowser = false;
  showPicflowBranding: boolean;
  downloadingAll = false;
  hideCover: boolean;
  hideFooter: boolean;
  blockAssetView: boolean;
  oldEmbedMode: boolean;
  galleryDescription: string;
  darkGalleryBackground: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public featureFlags: FeatureFlagsService,
    private browserService: BrowserService,
    private cleanUserGeneratedHtmlService: CleanUserGeneratedHtmlService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.route.data.subscribe(() => {
      this.gallery = this.route.snapshot.parent.data.gallery;
      this.tenant = this.gallery['tenant'];
      this.sections = this.route.snapshot.data.sections;
      this.hideCover = this.route.snapshot.queryParams.hideCover === 'true';
      this.hideFooter = this.route.snapshot.queryParams.hideFooter === 'true';
      this.blockAssetView =
        this.route.snapshot.queryParams.blockAssetView === 'true' ||
        this.route.snapshot.queryParams.blockImageView === 'true';
      if (isPlatformServer(this.platformId)) return;
      console.debug(this.gallery); // Because in production the gallery is not accessible due to SSR
      this.platformBrowser = true;
      this.showPicflowBranding = this.gallery['tenant_product_name'] === 'Picflow Free';
      this.galleryDescription = this.cleanUserGeneratedHtmlService.clean(this.gallery.description);
      this.calcDarkBackground();
      if (this.gallery.default_title_typography?.font_family) {
        loadFont(this.gallery.default_title_typography.font_family);
      }
      if (this.gallery.default_paragraph_typography?.font_family) {
        loadFont(this.gallery.default_paragraph_typography.font_family);
      }
      if (this.gallery.cover.title_typography?.font_family) {
        loadFont(this.gallery.cover.title_typography.font_family);
      }
      if (this.gallery.cover.paragraph_typography?.font_family) {
        loadFont(this.gallery.cover.paragraph_typography.font_family);
      }
    });

    if (isPlatformServer(this.platformId)) return;

    this.oldEmbedMode = (window['embedVersion'] ?? 1) === 1;

    this.sendHeightToParent();
    let oldHeight = document.body.clientHeight;
    const resizeObserver = new ResizeObserver(async () => {
      await sleep(0);
      const newHeight = document.body.clientHeight;
      if (Math.abs(newHeight - oldHeight) < 3) return; // Ignore small changes
      oldHeight = newHeight;
      this.sendHeightToParent();
    });
    resizeObserver.observe(document.body);

    // just in case the resize observer doesn't work
    sleep(1_000).then(() => this.sendHeightToParent());
    sleep(3_000).then(() => this.sendHeightToParent());
    sleep(5_000).then(() => this.sendHeightToParent());

    this.router.events.subscribe(event => {
      if (!(event instanceof NavigationStart)) return;
      const fullscreen = this.browserService.urlIsAssetUrl(event.url);
      window['frameCommsEvent']?.source.postMessage({ fullscreen, galleryId: this.gallery.id }, '*');
    });
  }

  private sendHeightToParent() {
    window['frameCommsEvent']?.source.postMessage(
      { FrameHeight: document.body.clientHeight, galleryId: this.gallery.id },
      '*'
    );
  }

  async ngOnInit() {
    if (!this.platformBrowser) return;
  }

  // scrollToGallery(element: HTMLElement) {
  //   element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  // }

  toAssetSection(section: SectionDto) {
    return <AssetSectionDto>section;
  }
  toAlbumSection(section: SectionDto) {
    return <GallerySectionDto>section;
  }
  toTextSection(section: SectionDto) {
    return <TextSectionDto>section;
  }
  toEmbedSection(section: SectionDto) {
    return <EmbedSectionDto>section;
  }

  niceSite(site: string) {
    if (!site) return '';
    if (site.split('//').length === 1) return site;
    return site.split('//')[1];
  }

  getFontSize(size: number) {
    if (size === 1) return 'small';
    if (size === 2) return 'medium';
    if (size === 3) return 'large';
    if (size === 4) return 'huge';
  }

  calcDarkBackground() {
    const rgb = parseColor(this.gallery.layout_background_color) || { r: 255, g: 255, b: 255 };
    this.darkGalleryBackground = isDarkColor(rgb.r, rgb.g, rgb.b);
  }

  getVideoUuid() {
    const [uuid] = this.gallery.cover.uuid.split('|');
    return uuid;
  }

  getVideoOrigin() {
    return this.gallery.cover.uuid.split('|')[1];
  }
}
